import { useMemo, useState } from 'react';
import { openReportPreview } from '../api/client';
import { useReportSends, useSendReports } from '../api/hooks';
import type { CrmUser, ReportSend } from '../api/types';
import { ErrorBanner, Modal, formatDateTime } from '../components/ui';

type Preset = 'last_month' | 'this_month' | 'last_7' | 'last_30' | 'custom';

const PRESETS: Array<{ value: Preset; label: string }> = [
  { value: 'last_month', label: 'Last month' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_7', label: 'Last 7 days' },
  { value: 'last_30', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom' },
];

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Local dates for a preset; reports never run into the future. */
function presetRange(p: Exclude<Preset, 'custom'>): { from: string; to: string } {
  const today = new Date();
  const days = (n: number) => new Date(today.getFullYear(), today.getMonth(), today.getDate() - n);
  switch (p) {
    case 'last_month':
      return {
        from: ymd(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        to: ymd(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
    case 'this_month':
      return { from: ymd(new Date(today.getFullYear(), today.getMonth(), 1)), to: ymd(today) };
    case 'last_7':
      return { from: ymd(days(6)), to: ymd(today) };
    case 'last_30':
      return { from: ymd(days(29)), to: ymd(today) };
  }
}

const STATUS: Record<ReportSend['status'], { label: string; color: string }> = {
  queued: { label: 'Waiting', color: 'var(--muted)' },
  sending: { label: 'Sending…', color: 'var(--accent)' },
  sent: { label: '✓ Sent', color: 'var(--good)' },
  failed: { label: '✗ Failed', color: 'var(--danger)' },
};

/**
 * Send performance reports to one or many people. Each gets their own PDF by
 * email; a team specialist's manager is copied. A test address can stand in
 * for every recipient while trying it out.
 */
export function SendReportModal({ users, onClose }: { users: CrmUser[]; onClose: () => void }) {
  const send = useSendReports();
  const [preset, setPreset] = useState<Preset>('last_month');
  const [custom, setCustom] = useState(() => presetRange('last_month'));
  const [note, setNote] = useState('');
  const [testMode, setTestMode] = useState(false);
  const [testEmail, setTestEmail] = useState('sisyatech@sisyaclass.com');
  const [error, setError] = useState<unknown>(null);
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  const { data: batchRows } = useReportSends(batchId);
  // Only this dialog's own batch: before sending, the shared cache holds the
  // recent-reports log, which must not read as this send's progress.
  const progress = batchId ? (batchRows ?? []) : [];

  const range = preset === 'custom' ? custom : presetRange(preset);
  const today = ymd(new Date());
  const rangeError =
    !range.from || !range.to ? 'Choose both dates.' : range.from > range.to ? 'The start date is after the end date.' : range.to > today ? 'The period cannot end in the future.' : null;

  const pending = progress.filter((r) => r.status === 'queued' || r.status === 'sending').length;
  const sent = progress.filter((r) => r.status === 'sent').length;
  const failed = progress.filter((r) => r.status === 'failed').length;
  const byUser = useMemo(() => new Map(progress.map((r) => [r.user_id, r])), [progress]);

  async function preview(user: CrmUser) {
    setError(null);
    setPreviewing(user.id);
    try {
      await openReportPreview({ user_id: user.id, from: range.from, to: range.to, note: note.trim() || undefined });
    } catch (err) {
      setError(err);
    } finally {
      setPreviewing(null);
    }
  }

  async function submit() {
    setError(null);
    try {
      const res = await send.mutateAsync({
        user_ids: users.map((u) => u.id),
        from: range.from,
        to: range.to,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(testMode ? { test_email: testEmail.trim() } : {}),
      });
      setBatchId(res.batch_id);
    } catch (err) {
      setError(err);
    }
  }

  const started = batchId !== null;
  const plural = users.length === 1 ? 'report' : 'reports';

  return (
    <Modal
      title={users.length === 1 ? `Performance report — ${users[0]!.full_name}` : `Send performance reports (${users.length})`}
      onClose={onClose}
      width={640}
      footer={
        started ? (
          <button className="btn btn-primary" onClick={onClose} disabled={pending > 0}>
            {pending > 0 ? `Sending… ${sent + failed} of ${progress.length}` : 'Done'}
          </button>
        ) : (
          <>
            <button className="btn" type="button" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-primary"
              disabled={send.isPending || Boolean(rangeError) || (testMode && !testEmail.trim())}
              onClick={submit}
            >
              {send.isPending ? <span className="spinner" /> : testMode ? `Send ${users.length} test ${plural}` : `Send ${users.length} ${plural}`}
            </button>
          </>
        )
      }
    >
      <ErrorBanner error={error} />

      {started && (
        <div className={`banner ${pending ? 'banner-info' : failed ? 'banner-error' : 'banner-success'}`} role="status">
          <div>
            {pending
              ? `Sending ${progress.length} ${plural}… you can close this window; sending continues in the background.`
              : `${sent} sent${failed ? `, ${failed} failed` : ''}.`}
          </div>
        </div>
      )}

      {!started && (
        <>
          <div className="field">
            <label>Reporting period</label>
            <div className="seg" role="radiogroup" aria-label="Reporting period">
              {PRESETS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  role="radio"
                  aria-checked={preset === p.value}
                  className={preset === p.value ? 'active' : ''}
                  onClick={() => setPreset(p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {preset === 'custom' ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <input type="date" aria-label="From" value={custom.from} max={today} onChange={(e) => setCustom({ ...custom, from: e.target.value })} />
                <span style={{ color: 'var(--muted)' }}>to</span>
                <input type="date" aria-label="To" value={custom.to} max={today} onChange={(e) => setCustom({ ...custom, to: e.target.value })} />
              </div>
            ) : (
              <div className="field-hint">{range.from} to {range.to}, compared with the same number of days before it.</div>
            )}
            {rangeError && <div className="err">{rangeError}</div>}
          </div>

          <div className="field">
            <label htmlFor="rp-note">Note from management <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(optional)</span></label>
            <textarea
              id="rp-note"
              rows={3}
              maxLength={1000}
              placeholder="Printed in the report and the email, e.g. “Great month — let's clear overdue follow-ups next.”"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <div className="field">
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600 }}>
              <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} />
              Send to a test address instead
            </label>
            {testMode ? (
              <>
                <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} style={{ marginTop: 6 }} />
                <div className="field-hint">Every report goes only to this address, marked [TEST]. Nobody else is emailed and managers are not copied.</div>
              </>
            ) : (
              <div className="field-hint">Each person gets their own report at their registered email; a team specialist's manager is copied.</div>
            )}
          </div>
        </>
      )}

      <div className="field" style={{ marginBottom: 0 }}>
        <label>{started ? 'Progress' : `Recipients (${users.length})`}</label>
        <div className="recipients">
          {users.map((u) => {
            const row = byUser.get(u.id);
            return (
              <div className="recipient" key={u.id}>
                <div style={{ minWidth: 0 }}>
                  <b>{u.full_name}</b>
                  <span className="role">{u.role?.name ?? '—'}{u.role?.key === 'manager' || u.role?.key === 'team_lead' ? ' · team report' : ''}</span>
                  <div className="to">
                    {row ? <>→ {row.recipient_email}{row.cc_emails.length ? ` · cc ${row.cc_emails.join(', ')}` : ''}</> : testMode ? `→ ${testEmail || 'test address'}` : `→ ${u.email}`}
                  </div>
                  {row?.error && <div className="err" style={{ marginTop: 2 }}>{row.error}</div>}
                </div>
                {row ? (
                  <span style={{ color: STATUS[row.status].color, fontWeight: 600, whiteSpace: 'nowrap' }}>{STATUS[row.status].label}</span>
                ) : (
                  <button className="link-button" disabled={Boolean(rangeError) || previewing !== null} onClick={() => void preview(u)}>
                    {previewing === u.id ? 'Opening…' : 'Preview PDF'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

/** The last reports sent from this organisation, under the Users table. */
export function RecentReports() {
  const { data: rows = [], isPending } = useReportSends(null, true);
  if (isPending || rows.length === 0) return null;
  return (
    <div className="card" style={{ marginTop: 18 }}>
      <div className="card-head"><h2>Performance reports sent</h2></div>
      <div className="card-body" style={{ paddingTop: 4 }}>
        <table className="data">
          <thead>
            <tr><th>Person</th><th>Period</th><th>Sent to</th><th>Status</th><th>By</th><th>When</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td><strong>{r.full_name}</strong>{r.is_test && <span className="tag" style={{ marginLeft: 6 }}>test</span>}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{r.period_from} → {r.period_to}</td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {r.recipient_email}{r.cc_emails.length ? <div style={{ color: 'var(--muted)' }}>cc {r.cc_emails.join(', ')}</div> : null}
                </td>
                <td title={r.error ?? undefined} style={{ color: STATUS[r.status].color, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {STATUS[r.status].label}
                  {r.error && <div style={{ fontWeight: 400, fontSize: 12, color: 'var(--muted)', whiteSpace: 'normal' }}>{r.error}</div>}
                </td>
                <td>{r.sent_by_name}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.sent_at ?? r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
